
import {TopicConfiguration} from "../config/topic-configuration";
import {Kiwis, Station} from "./index";
import {AppState, MessageBoxType} from "../components/app/app-mobx";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import {reaction} from "mobx";
import {UiState} from "../core/ui-state";
import {MapState} from "../core/map-state";
import {FeatureLayerConfiguration} from "../core/config/layer-configuration";


export class StationFinder {
    appStore: AppState;
    kiwis: Kiwis;
    mapStore: MapState;
    uiStore: UiState;
    layersConfig: FeatureLayerConfiguration[];

    constructor(appStore: AppState, kiwis: Kiwis, layersConfig: FeatureLayerConfiguration[],
                mapStore: MapState, uiStore: UiState) {
        this.appStore = appStore;
        this.kiwis = kiwis;
        this.layersConfig = layersConfig;
        this.mapStore = mapStore;
        this.uiStore = uiStore;
    }

    // TODO; move to another class and file
    searchAndShowStation(stationNo: string, topic: TopicConfiguration) {
        this.appStore.showMessageBox({
            title: "Station wird gesucht...",
            message: `Die Station ${stationNo} wird gesucht.`,
            hasProgress: true,
            percent: 30
        });

        // search station object, select in kiwis store and open popup
        const disposeStationsLoaded = reaction(
            () => [...this.kiwis.stations.entries()].map((entry) => entry[1]),
            (stations) => {
                if (!stations) {
                    return;
                }

                disposeStationsLoaded();

                this.appStore.showMessageBox({
                    title: "Station wird gesucht...",
                    message: "Stationen in Karte geladen.",
                    hasProgress: true,
                    percent: 50
                });

                // find station by stationNo
                // base IDs of current topic have to be respected
                const station = stations.find((st: Station) => (
                    st.no.toLowerCase() === stationNo.toLowerCase() && topic.baseIds.includes(st.baseId)
                ));

                if (!station) {
                    this.appStore.showMessageBox({
                        type: MessageBoxType.Error,
                        title: "Fehler beim Laden der Station",
                        subTitle: stationNo,
                        message: `Messstation ${stationNo} nicht gefunden.`,
                        hasProgress: false
                    });
                    return;
                }

                this.showStation(station);
            }
        );
    }

    /**
     * Highlight the station feature on the map, open station popup
     *
     * @param station
     */
    private showStation(station: Station) {
        // select station and open popup
        this.kiwis.selectStation(station.oid);
        this.uiStore.openStationPopup(0);

        // zoom to station feature and highlight feature on map
        const disposeLayerLoaded = reaction(
            () => [...this.mapStore.layers.entries()].map((entry) => entry[1]),
            (layers) => {
                if (!layers || !this.mapStore || !this.mapStore.view) {
                    return;
                }

                // wait until all layers are loaded
                if (layers.length !== this.layersConfig.length) {
                    return;
                }

                disposeLayerLoaded();

                const queryLayerNames = [
                    "hydroStationen",
                    "FLGWAE_NAEHRST",
                    "BADEN_QUALI",
                    "GRNDW_QUALI",
                    "bodenStationen"
                ];
                const stationLayers: any[] = queryLayerNames.map((layerName) => {
                    const stationLayer = layers.find((lyr) => lyr.id === layerName);
                    if (!stationLayer) {
                        const error = `Station layer with ID ${this.layersConfig[0].id} not found`;
                        // eslint-disable-next-line no-console
                        console.error(error);
                        throw new Error(error);
                    }
                    return stationLayer;
                });

                this.appStore.showMessageBox({
                    title: "Station wird gesucht...",
                    message: "Kartenansicht geladen. Suche Station in Karte...",
                    hasProgress: true,
                    percent: 60
                });

                const layerUpdatePointers: any[] = [];
                stationLayers.forEach((lyr: any) => {
                    layerUpdatePointers.push(this.getFeatureLayerPointer(lyr));
                });

                // search station feature on all layers as soon as feature layer pointer is ready and
                // found on layer view
                Promise.all(layerUpdatePointers).then((results: any[]) => {
                    this.appStore.showMessageBox({
                        title: "Station wird gesucht...",
                        message: "Layeransichten geladen. Suche Station in Karte...",
                        hasProgress: true,
                        percent: 70
                    });
                    this.queryStationLayers(results, station);
                });
            }
        );

    }

    /**
     * Watch for layer view updates to get a pointer to the feature layer and view
     */
    private getFeatureLayerPointer(stationLayer: any) {
        return new Promise((resolve, reject) => {
            if (!this.mapStore || !this.mapStore.view) {
                reject("View is undefined");
            }
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.mapStore.view.whenLayerView(stationLayer).then((layerView: any) => {
                const handle = layerView.watch("updating", (value: any) => {
                    if (!value) {
                        // wait for the layer view to finish updating
                        handle.remove();

                        resolve({
                            layerId: stationLayer.id,
                            layer: stationLayer,
                            layerView
                        });
                    }
                });
            });
        });
    }

    /**
     * Query multiple station layers for a station
     *
     * @param layerPointers
     * @param station
     * @private
     */
    private queryStationLayers(layerPointers: any[], station: Station) {
        const layerQueries: any[] = [];
        const layers = layerPointers.map((l) => l.layer);
        layers.forEach((lyr: any) => {
            layerQueries.push(this.queryStationLayer(lyr as FeatureLayer, station));
        });

        this.appStore.progressMessageBox(80);

        // search for station feature in the view
        Promise.all(layerQueries).then((results: any[]) => {
            if (!results || !results.length) {
                this.appStore.showMessageBox({
                    type: MessageBoxType.Error,
                    title: "Fehler beim Abfragen der Layer",
                    subTitle: station.no,
                    message: `Messstation ${station.no} auf keinem Layer gefunden.`,
                    hasProgress: false
                });
                return;
            }

            // only one feature should be returned, because feature should only be included on ony feature layer
            const result = results.filter((g) => !!g)[0];
            const {feature, layerId} = result;
            if (!feature) {
                this.appStore.showMessageBox({
                    type: MessageBoxType.Error,
                    title: "Fehler beim Abfragen der Layer",
                    subTitle: station.no,
                    message: `Messstation ${station.no} auf keinem Layer gefunden.`,
                    hasProgress: false
                });
                return;
            }

            // get related layer and view
            const layerPointer = layerPointers.filter((l) => l.layerId === layerId)[0];

            // Zoom to station in map, highlight station feature
            setTimeout(() => this.appStore.closeMessageBox(), 500);

            this.mapStore.unhighlightFeature();

            const highlighted = layerPointer.layerView.highlight(feature);
            this.mapStore.highlightFeature(highlighted);

            if (this.mapStore.view) {
                this.mapStore.view.goTo(feature);
            }

            this.appStore.showMessageBox({
                title: "Station gefunden",
                message: "Laden der Station abgeschlossen.",
                hasProgress: true,
                percent: 100
            });
        });
    }

    /**
     * Query station layer to find a station
     *
     * @param stationLayer
     * @param station
     * @private
     */
    private queryStationLayer(stationLayer: FeatureLayer, station: Station) {
        return new Promise((resolve, reject) => {
            stationLayer
                .queryFeatures({
                    where: `oid = ${station.oid}`,
                    outFields: ["oid"],
                    returnGeometry: true
                })
                .then((results: __esri.FeatureSet) => {
                    this.appStore.progressMessageBox(90);
                    const feature = results.features[0];
                    if (!feature) {
                        resolve(null);
                    }

                    resolve({
                        layerId: stationLayer.id,
                        feature
                    });
                }).catch((error) => {
                    reject(error);
                });
        });
    }
}
